/** Angular */
import { Injectable } from '@angular/core';
import {
  Router,
  CanActivateChild,
  CanActivate,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
/** Sixense */
import { BydLocalStorage } from '@sixense/ds-lib-utils';
/** Services */
import { BydAuthService } from '../services/byd-auth.service';
/** Enums */
import { BydAuthGuardPathEnum } from '../enums/byd-auth-guard-path.enum';
import { BydAuthStorageKeysEnum } from '../enums/byd-auth-storage-keys.enum';

@Injectable({
  providedIn: 'root',
})
/**
 * Auth guard making sure the user is logged in before activating specific routes.
 * Can either be used for canActivate or canActivateChild.
 */
export class BydAuthGuard implements CanActivate, CanActivateChild {
  /**
   * Creates an instance of DsAuthGuard.
   *
   * @param router contains the information about the associated route
   * @param bydAuthService the byd authentication service
   */
  constructor(private router: Router, private bydAuthService: BydAuthService) {}

  /**
   * Deciding if a route can be activated,
   * It is run before the next route is activated.
   *
   * @returns boolean | UrlTree
   * @param state the route snapshot state
   * @param route contains the information about the associated route
   */
  public canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.getGuardState(route, state);
  }

  /**
   * Deciding if a route can be activated,
   * It is run before each child route is activated.
   *
   * @returns boolean | UrlTree
   * @param state the route snapshot state
   * @param route contains the information about the associated route
   */
  public canActivateChild(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    return this.getGuardState(route, state);
  }

  /**
   * If `bydAuthService` returns `true`, navigation will continue.
   * Else current navigation will be cancelled,
   * and a new navigation will be kicked off to the `UrlTree` returned from the guard.
   *
   * @param route contains the information about the associated route
   * @param state the route snapshot state
   * @returns boolean | UrlTree
   */
  private getGuardState(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    if (!this.bydAuthService.isLoggedIn) {
      const redirectToKey = route.data?.redirectToKey || BydAuthStorageKeysEnum.REDIRECT_TO;
      BydLocalStorage.setItem(redirectToKey, state.url);
      return this.router.createUrlTree([route.data.path || BydAuthGuardPathEnum.login], {
        queryParams: { [redirectToKey]: state.url },
      });
    }
    return true;
  }
}
